import { useMutation, useQueryClient } from "@tanstack/react-query";
import GlobalAppAPI from "../../api/GlobalAppAPI";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { FileInterface } from "../../../types/RecordInterfaces";
import { KeyFeature } from "../../../types/GlobalInterfaces";

// This is just a payload. The actual cloud app will be VERY different.
// A lot happens on the API after passing this payload to it.
export interface GlobalAppPayload {
  Icon?: string | FileInterface;
  Name: string;
  Description: string;
  Summary: string;
  Pages: string[];
  OriginCloudID: string;
  CloudAppID: string;
  GlobalAppID?: string;
  DefaultPage?: string;
  Images: Array<any>;
  CoverImage?: FileInterface;
  KeyFeatures: KeyFeature[];
  MSTags: Array<{ Title: string }>;
  InstallationRequirements: Array<{ Title: string }>;
  ReleaseNotes?: Array<{ Title: string }>;
}

interface Props {
  payload: GlobalAppPayload;
  onSuccess?: () => void;
}

const useUploadGlobalApp = () => {
  const client = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const uploadGlobalApp = useMutation({
    mutationFn: async ({ payload }: Props) => {
      if (!workspaceID) return Promise.reject("Unexpected error occurred.");

      newToastEvent("success", "Deploying your app...", "UploadingGlobalApp", {
        loading: true,
        autoClose: false,
      });

      return GlobalAppAPI.uploadGlobalApp(payload, workspaceID);
    },
    onSuccess: (_, { payload, onSuccess }) => {
      client.invalidateQueries({ queryKey: ["workspace"] });
      client.invalidateQueries({ queryKey: ["cloudApps"] });
      client.invalidateQueries({ queryKey: ["globalApps"] });
      client.removeQueries({ queryKey: ["globalApp"] });

      updateToastEvent(
        "success",
        `Successfully uploaded ${
          payload.Name || "App"
        }! It is now visible across all clouds.`,
        "UploadingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      updateToastEvent(
        "error",
        `Failed to deploy your app! ${getErrorMessage(error)}`,
        "UploadingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );
    },
  });

  return {
    uploadGlobalApp: uploadGlobalApp.mutate,
    uploadingApp: uploadGlobalApp.isPending,
  };
};

export default useUploadGlobalApp;
